// Shared by PDP, quick view and the individual product forms on listings.
(() => {
if (customElements.get('delivery-time')) return;

class DeliveryTime extends HTMLElement {
  connectedCallback() {
    this.render();
  }

  render() {
    const cutoffHour = parseInt(this.dataset.cutoffHour || '10', 10);
    const shippingDays = parseInt(this.dataset.shippingDays || '1', 10);
    const textPrefix = this.dataset.textPrefix || 'U Ciebie w domu za:';
    const customDeliveryDate = this.dataset.deliveryDate || null;
    const isOutOfStock = this.dataset.outOfStock === 'true';
    const fallbackDays = parseInt(this.dataset.fallbackDays || '10', 10);
    const locale = (this.dataset.locale || 'pl').toLowerCase().split('-')[0];

    const translations = {
      pl: { today: 'dzisiaj', tomorrow: 'jutro (1 dzień)', days: 'dni' },
      de: { today: 'heute', tomorrow: 'morgen (1 Tag)', days: 'Tage' },
      en: { today: 'today', tomorrow: 'tomorrow (1 day)', days: 'days' },
    };
    const t = translations[locale] || translations.pl;

    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Warsaw' }));
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    // Bundle: if a list of component products is provided, compute the delivery
    // time for each component and take the LONGEST one. Only the components from
    // the metafield drive the result (the bundle product itself is ignored).
    let diffDays = null;
    const bundleItems = this.parseBundleItems();

    if (bundleItems.length > 0) {
      diffDays = bundleItems.reduce((max, item) => {
        const itemDiff = this.computeDiffDays({
          now,
          today,
          cutoffHour,
          shippingDays,
          fallbackDays,
          isOutOfStock: item.oos === true,
          customDeliveryDate: item.date || null,
        });
        return itemDiff > max ? itemDiff : max;
      }, -Infinity);
    } else {
      diffDays = this.computeDiffDays({
        now,
        today,
        cutoffHour,
        shippingDays,
        fallbackDays,
        isOutOfStock,
        customDeliveryDate,
      });
    }

    let displayText;
    if (diffDays === 0) {
      displayText = t.today;
    } else if (diffDays === 1) {
      displayText = t.tomorrow;
    } else if (diffDays >= 2 && diffDays <= 4) {
      displayText = `${diffDays} ${t.days}`;
    } else {
      displayText = `${diffDays} ${t.days}`;
    }

    this.textContent = `${textPrefix} ${displayText}`;

    // Persist the actual PDP result, including bundle/market rules, as an
    // absolute calendar date. Never add these days to a later order-view date.
    const arrival = new Date(today);
    arrival.setDate(arrival.getDate() + diffDays);
    this.snapshot = {
      _bellamy_delivery_date: [arrival.getFullYear(), String(arrival.getMonth() + 1).padStart(2, '0'), String(arrival.getDate()).padStart(2, '0')].join('-'),
      _bellamy_delivery_days: String(diffDays),
    };
    const form = this.dataset.form ? document.getElementById(this.dataset.form) : null;
    if (form?.querySelector('[name="id"]')?.value === this.dataset.variantId) {
      DeliveryTime.writeSnapshot(form, this.snapshot);
    }
  }

  static forForm(form) {
    const delivery = form?.closest?.('product-info')?.querySelector('delivery-time');
    return delivery && delivery.dataset.form === form.id && delivery.dataset.variantId === form.querySelector('[name="id"]')?.value
      ? delivery : null;
  }

  static writeSnapshot(form, snapshot) {
    form.querySelectorAll('[data-delivery-snapshot]').forEach((input) => input.remove());
    for (const [key, value] of Object.entries(snapshot || {})) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${key}]`;
      input.value = value;
      input.setAttribute('data-delivery-snapshot', '');
      form.append(input);
    }
  }

  static async loadSnapshot(productUrl, variantId) {
    if (!productUrl || !variantId) return null;
    const url = new URL(productUrl, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    url.searchParams.set('variant', variantId);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const response = await fetch(url, {signal: controller.signal});
      if (!response.ok) throw new Error(`Delivery lookup failed: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = Array.from(html.querySelectorAll('product-info delivery-time'))
        .find((node) => node.dataset.variantId === String(variantId));
      if (!source) return null;
      // Read only the selected PDP block's data; never execute fetched scripts.
      const delivery = document.createElement('delivery-time');
      Object.assign(delivery.dataset, source.dataset);
      delete delivery.dataset.form;
      delivery.render();
      return delivery.snapshot;
    } catch (error) {
      // Keep add-to-cart usable. The account extension retains its metafield
      // fallback for items without a snapshot, including a failed PDP lookup.
      console.warn('Delivery snapshot unavailable:', error);
      return null;
    } finally {
      clearTimeout(timeout);
    }
  }

  static productUrl(form) {
    if (!form || typeof form === 'string') return form;
    if (form.dataset.productUrl) return form.dataset.productUrl;
    const card = form.closest('.product-card, .horizontal-product, .hotspot');
    if (card) return card.querySelector('a[href*="/products/"]')?.href || card.href
      || card.querySelector('[data-product-url]')?.dataset.productUrl;
    return form.closest('product-info, product-card-info')?.dataset.productUrl;
  }

  // All cart entry points use this one adapter. `source` is an existing form,
  // product card or product URL; only Shopify item fields leave this method.
  static async prepareItems(items) {
    return Promise.all(items.map(async ({source, ...item}) => {
      const delivery = DeliveryTime.forForm(source);
      if (delivery) delivery.render();
      const snapshot = delivery?.snapshot || await DeliveryTime.loadSnapshot(DeliveryTime.productUrl(source), item.id);
      return {...item, properties: {...item.properties, ...snapshot}};
    }));
  }

  static async prepare(target, callback) {
    if (target.deliveryPending || target.hasAttribute('aria-disabled')) return null;
    target.deliveryPending = true;
    const inert = target.inert || false;
    target.inert = true;
    target.setAttribute('aria-busy', 'true');
    try { return await callback(); }
    finally {
      target.deliveryPending = false;
      target.inert = inert;
      target.removeAttribute('aria-busy');
    }
  }

  static async prepareForm(form) {
    const id = form.querySelector('[name="id"]').value;
    const bundles = form.bundles;
    const ids = bundles.map((input) => input.value).join(',');
    const items = await DeliveryTime.prepareItems([
      {id, source: form}, ...bundles.map((input) => ({id: input.value, source: input}))
    ]);
    if (id !== form.querySelector('[name="id"]').value || ids !== form.bundles.map((input) => input.value).join(',')) return false;
    DeliveryTime.writeSnapshot(form, items[0].properties);
    bundles.forEach((input, index) => { input.deliverySnapshot = items[index + 1].properties; });
    return true;
  }

  static syncFormData(form, data) {
    const delivery = DeliveryTime.forForm(form);
    if (!delivery) return;
    delivery.render();
    for (const [key, value] of Object.entries(delivery.snapshot)) data.set(`properties[${key}]`, value);
  }

  // /cart/update cannot attach line properties. New quick-order variants use
  // /cart/add; existing quantities keep /cart/update and their original promise.
  static async updateQuantities(form, quantities, sections) {
    const updates = {...quantities};
    const source = form.getAttribute('data-product-url');
    const options = {sections, sections_url: source};
    const additions = Array.from(form.allVariants).flatMap((input) => {
      const id = input.getAttribute('data-quantity-variant-id');
      return updates[id] > 0 && Number(input.getAttribute('data-cart-quantity')) === 0
        ? [{id, quantity: updates[id], source}] : [];
    });
    const post = async (url, body) => (await fetch(url, {...theme.utils.fetchConfig(), body: JSON.stringify({...options, ...body})})).json();
    let added;
    if (additions.length) {
      added = await post(theme.routes.cart_add_url, {items: await DeliveryTime.prepareItems(additions)});
      if (added.status) return {errors: added.description || added.message};
      additions.forEach(({id}) => delete updates[id]);
    }
    if (Object.keys(updates).length) return post(theme.routes.cart_update_url, {updates});
    const cart = await (await fetch(theme.routes.cart_url, theme.utils.fetchConfig('json', 'GET'))).json();
    return {...cart, sections: added?.sections};
  }

  parseBundleItems() {
    const raw = this.dataset.bundleItems;
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  computeDiffDays({ now, today, cutoffHour, shippingDays, fallbackDays, isOutOfStock, customDeliveryDate }) {
    // Out of stock: base delivery date comes from the metafield (custom.delivery_time, Date).
    // On top of that restock/dispatch date we still add the per-market shipping time
    // (shippingDays = value resolved per country in Liquid), so the customer sees the
    // real arrival date = restock date + shipping transit days.
    let diffDays = null;

    if (customDeliveryDate) {
      const [year, month, day] = customDeliveryDate.split('-').map(Number);
      const metafieldDate = new Date(year, month - 1, day);
      metafieldDate.setHours(0, 0, 0, 0);

      // The delivery_time date is treated as END OF DAY: it stays valid for the
      // whole entered day and is considered "passed" only from the next day on
      // (i.e. when today is strictly after the metafield date).
      if (metafieldDate >= today) {
        // Add per-market shipping (business) days to the restock date
        const arrivalDate = this.addBusinessDays(metafieldDate, shippingDays);
        arrivalDate.setHours(0, 0, 0, 0);

        diffDays = Math.round((arrivalDate - today) / (1000 * 60 * 60 * 24));
      } else if (isOutOfStock) {
        // The metafield delivery date already passed, but the product is still
        // out of stock. Instead of falling back to the in-stock (fast) shipping
        // logic — which would wrongly promise delivery in ~1 day — show a fixed
        // fallback estimate (default 10 days, configurable via the block setting).
        diffDays = fallbackDays;
      }
    }

    // In stock (or no/invalid date): calculate from shipping days logic
    if (diffDays === null) {
      const dayOfWeek = now.getDay();
      const hour = now.getHours();
      const isBusinessDay = dayOfWeek >= 1 && dayOfWeek <= 5;
      const isBeforeCutoff = hour < cutoffHour;

      let dispatchDate = new Date(now);

      if (isBusinessDay && isBeforeCutoff) {
        // Dispatch today
      } else {
        dispatchDate = this.addBusinessDays(dispatchDate, 1);
      }

      const deliveryDate = this.addBusinessDays(dispatchDate, shippingDays);
      const deliveryDay = new Date(deliveryDate);
      deliveryDay.setHours(0, 0, 0, 0);

      diffDays = Math.round((deliveryDay - today) / (1000 * 60 * 60 * 24));
    }

    return diffDays;
  }

  addBusinessDays(date, days) {
    let result = new Date(date);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      const dow = result.getDay();
      if (dow !== 0 && dow !== 6) {
        added++;
      }
    }
    return result;
  }
}

customElements.define('delivery-time', DeliveryTime);

})();
